import { Button } from '@creditornot/cb-button';
import { InfoCircleFilled } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React from 'react';
import { Field, useForm } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { BasketDiscountType, CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { useCampaignModuleContext } from 'modules/campaigns/context';

import { CampaignsTargetContent } from '../../../utils/telemetry/constants';
import {
  telemetryNewCampaignInteractionClickCampaignRules,
  telemetryUpdateCampaignInteractionClickCampaignUpdate,
} from '../../../utils/telemetry/telemetry';
import { TestIds } from '../../../utils/testUtils';
import CustomRadioCard from '../../CustomRadioCard';
import { DiscountAmountLabel } from '../DiscountAmountLabel';
import { NO_UPPER_LIMIT_OPTION } from '../constants';
import { DiscountAmountFormValues, UpperLimitFormField } from '../types';

export type DiscountAmountFormModalProps = {
  discountType: BasketDiscountType;
  discountValueOptions: {
    [BasketDiscountType.AMOUNT]: number[];
    [BasketDiscountType.PERCENTAGE]: number[];
  };
  upperLimitOptions: number[];
  recommendedDiscountValue: {
    [BasketDiscountType.AMOUNT]: number;
    [BasketDiscountType.PERCENTAGE]: number;
  };
  recommendedUpperLimit: number | null;
  minBasketSize: number;
  campaignType: CampaignType;
  isNewCampaignRoute?: boolean;
  onCloseClick: () => void;
  onUpdate: () => void;
};

const StyledRoot = styled.div`
  background-color: ${cbSemanticCssVars.surfaceMain};
  width: 800px;
  border-radius: 8px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: auto;
    width: 98vw;
  }
`;

const StyledModalHeader = styled(ModalHeader)`
  padding: 16px;
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`;

const StyledSection = styled.div`
  padding: 16px;

  &:not(:last-of-type) {
    border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  padding: 16px;
`;

const StyledTitle = styled(Typography.Body2).attrs({ fontWeight: 'bold' })`
  margin-block-end: 16px;
  display: block;
`;

const StyledSectionContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  > div {
    flex: 1;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StyledIcon = styled(InfoCircleFilled).attrs({
  width: 16,
  height: 16,
})`
  fill: ${cssThemedValue({ light: colors.pepper24, dark: colors.salt72 })};
`;

const StyledNoteWrapper = styled.p`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  margin: 0;
  background: ${cssThemedValue({ light: colors.pepper4, dark: colors.salt8 })};
  border-radius: 8px;
  gap: 8px;
`;

export const DiscountAmountFormModal = ({
  discountType,
  discountValueOptions,
  upperLimitOptions,
  recommendedDiscountValue,
  recommendedUpperLimit,
  minBasketSize,
  campaignType,
  onCloseClick,
  onUpdate,
  isNewCampaignRoute,
}: DiscountAmountFormModalProps) => {
  const { getFieldState } = useForm();
  const { currency } = useCampaignModuleContext();

  const upperLimitValue: UpperLimitFormField = getFieldState('upperLimit')?.value || recommendedUpperLimit;

  return (
    <StyledRoot>
      <StyledModalHeader onCloseClick={onCloseClick}>
        <Typography.Heading6>
          <FormattedMessage id="campaigns.form.discount-amount.modal.title" />
        </Typography.Heading6>
      </StyledModalHeader>

      <StyledModalContent>
        <StyledSection>
          <StyledTitle fontWeight="bold">
            <FormattedMessage id="campaigns.form.discount-amount.modal.discount-amount" />
          </StyledTitle>

          <StyledSectionContent>
            {discountValueOptions[discountType].map(option => {
              const isRecommended = option === recommendedDiscountValue[discountType];

              return (
                <Field<DiscountAmountFormValues['discountAmount']>
                  key={option}
                  name="discountAmount"
                  type="radio"
                  value={option}
                >
                  {({ input }) => (
                    <CustomRadioCard {...input} onChange={() => input.onChange(option)} isRecommended={isRecommended}>
                      <Typography.Body2>
                        <DiscountAmountLabel discountAmount={option} discountType={discountType} />
                      </Typography.Body2>
                    </CustomRadioCard>
                  )}
                </Field>
              );
            })}
          </StyledSectionContent>
        </StyledSection>

        {discountType === BasketDiscountType.PERCENTAGE && (
          <>
            <StyledSection>
              <StyledTitle>
                <FormattedMessage id="campaigns.form.discount-amount.modal.upper-limit" />
              </StyledTitle>

              <StyledSectionContent>
                {[...upperLimitOptions, NO_UPPER_LIMIT_OPTION].map((option: UpperLimitFormField) => {
                  const isRecommended = option === recommendedUpperLimit;

                  return (
                    <Field<DiscountAmountFormValues['upperLimit']>
                      key={option}
                      name="upperLimit"
                      type="radio"
                      value={option}
                    >
                      {({ input }) => (
                        <CustomRadioCard
                          {...input}
                          isRecommended={isRecommended}
                          onChange={() => {
                            input.onChange(option);

                            if (isNewCampaignRoute) {
                              telemetryNewCampaignInteractionClickCampaignRules(
                                CampaignsTargetContent.NEW.CHANGE_UPPER_LIMIT,
                                campaignType,
                              );
                            } else {
                              telemetryUpdateCampaignInteractionClickCampaignUpdate(
                                CampaignsTargetContent.EDIT.CHANGE_UPPER_LIMIT,
                                campaignType,
                              );
                            }
                          }}
                        >
                          <Typography.Body2>
                            {option === NO_UPPER_LIMIT_OPTION ? (
                              <FormattedMessage id="campaigns.form.discount-amount.modal.no-limit" />
                            ) : (
                              <MonetaryAmount
                                amount={convertMonetaryIntToDecimal(option, currency)}
                                currency={currency}
                              />
                            )}
                          </Typography.Body2>
                        </CustomRadioCard>
                      )}
                    </Field>
                  );
                })}
              </StyledSectionContent>
            </StyledSection>

            <StyledSection>
              <StyledNoteWrapper>
                <StyledIcon />
                <Typography.Body3 data-testid={TestIds.FORM.DISCOUNT_AMOUNT.NOTE}>
                  {upperLimitValue === NO_UPPER_LIMIT_OPTION ? (
                    <FormattedMessage id="campaigns.form.discount-amount.modal.note-no-cap" />
                  ) : minBasketSize === 0 ? (
                    <FormattedMessage
                      id="campaigns.form.discount-amount.modal.note-cap-unlimited"
                      values={{
                        discountAmount: (
                          <MonetaryAmount
                            amount={convertMonetaryIntToDecimal(upperLimitValue, currency)}
                            currency={currency}
                          />
                        ),
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      id="campaigns.form.discount-amount.modal.note-cap"
                      values={{
                        minBasketSize: <MonetaryAmount amount={minBasketSize} currency={currency} />,
                        discountAmount: (
                          <MonetaryAmount
                            amount={convertMonetaryIntToDecimal(upperLimitValue, currency)}
                            currency={currency}
                          />
                        ),
                      }}
                    />
                  )}
                </Typography.Body3>
              </StyledNoteWrapper>
            </StyledSection>
          </>
        )}
      </StyledModalContent>

      <StyledModalFooter>
        <Button size="small" variant="blue" onClick={onUpdate}>
          <FormattedMessage id="campaigns.form.update" />
        </Button>
      </StyledModalFooter>
    </StyledRoot>
  );
};
